import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { absoluteSiteUrl, siteConfig } from '../../config/site'
import { getStaticSeo } from '../seo'
import type { Locale } from '../types'

function setMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) { element = document.createElement('meta'); document.head.appendChild(element) }
  Object.entries(attributes).forEach(([key, value]) => element!.setAttribute(key, value))
}
function setLink(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLLinkElement>(selector)
  if (!element) { element = document.createElement('link'); document.head.appendChild(element) }
  Object.entries(attributes).forEach(([key, value]) => element!.setAttribute(key, value))
}
function normalizePath(pathname: string) { return pathname === '/' || pathname.endsWith('/') ? pathname : `${pathname}/` }

export function TransportSEO({ locale, title, description, alternatePath, noIndex = false }: { locale: Locale; title?: string; description?: string; alternatePath?: string; noIndex?: boolean }) {
  const location = useLocation()
  const staticSeo = getStaticSeo(location.pathname)
  const pageTitle = title ?? staticSeo?.title ?? (locale === 'ar' ? 'Syria Bus Transport | نقل الباصات' : 'Syria Bus Transport')
  const pageDescription = description ?? staticSeo?.description ?? (locale === 'ar' ? 'بيانات شبكة الباصات المنشورة.' : 'Published bus network data.')
  const currentPath = normalizePath(location.pathname)
  const alternate = alternatePath ?? staticSeo?.alternatePath

  useEffect(() => {
    document.documentElement.lang = locale
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr'
    document.body.dataset.locale = locale
    document.title = pageTitle
    setMeta('meta[name="description"]', { name: 'description', content: pageDescription })
    setMeta('meta[name="robots"]', { name: 'robots', content: noIndex ? 'noindex,nofollow' : 'index,follow,max-snippet:-1,max-image-preview:large' })
    const currentUrl = absoluteSiteUrl(currentPath)
    setLink('link[rel="canonical"]', { rel: 'canonical', href: currentUrl })
    if (alternate) {
      const alternateUrl = absoluteSiteUrl(alternate)
      const arUrl = locale === 'ar' ? currentUrl : alternateUrl
      const enUrl = locale === 'en' ? currentUrl : alternateUrl
      setLink('link[rel="alternate"][hreflang="ar"]', { rel: 'alternate', hreflang: 'ar', href: arUrl })
      setLink('link[rel="alternate"][hreflang="en"]', { rel: 'alternate', hreflang: 'en', href: enUrl })
      setLink('link[rel="alternate"][hreflang="x-default"]', { rel: 'alternate', hreflang: 'x-default', href: arUrl })
    }
    setMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' })
    setMeta('meta[property="og:title"]', { property: 'og:title', content: pageTitle })
    setMeta('meta[property="og:description"]', { property: 'og:description', content: pageDescription })
    setMeta('meta[property="og:url"]', { property: 'og:url', content: currentUrl })
    setMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: siteConfig.siteName })
    let script = document.head.querySelector<HTMLScriptElement>('script[data-sbt-schema]')
    if (!script) { script = document.createElement('script'); script.type = 'application/ld+json'; script.dataset.sbtSchema = 'true'; document.head.appendChild(script) }
    script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': [
      { '@type': 'WebSite', '@id': `${siteConfig.siteUrl}/#website`, url: siteConfig.siteUrl, name: siteConfig.siteName, inLanguage: siteConfig.supportedLocales },
      { '@type': 'WebPage', '@id': `${currentUrl}#webpage`, url: currentUrl, name: pageTitle, description: pageDescription, inLanguage: locale, isPartOf: { '@id': `${siteConfig.siteUrl}/#website` } },
    ] })
  }, [alternate, currentPath, locale, noIndex, pageDescription, pageTitle])
  return null
}
